import React from "react";
import { useDispatch } from "react-redux";
import { swapInputs } from "src/store/features/swap.slice";
import { useUserSlice } from "src/store/features/user.slice";
import usePermission from "src/utils/hooks/usePermission";

const DEFAULT_SUBMIT_BUTTON = {
  color: "salmon",
  text: "Place Order",
  disabled: false,
};

const DEFAULT_CLEAR_BUTTON = {
  text: "Clear",
  isLoading: false,
  width: "72px",
};

const DEFAULT_USER_LOCKED = {
  isLocked: false,
  isBypassLock: false,
  isOpen: false,
};

interface useTradeUIProps {
  summaryIsLoading?: boolean;
  ctIsLoading?: boolean;
}

const useTradeUI = (props: useTradeUIProps) => {
  const { summaryIsLoading = false, ctIsLoading = false } = props;
  const dispatch = useDispatch();
  const { isGuest } = usePermission();
  const { isUnverified } = useUserSlice();

  // Button styles logic
  const [submitButton, setSubmitButton] =
    React.useState<Record<string, any>>(DEFAULT_SUBMIT_BUTTON);

  // clear button / loading button logic
  const [clearButton, setClearButton] = React.useState<Record<string, any>>(DEFAULT_CLEAR_BUTTON);

  // Swap icon logic
  const [hasInputsSwapped, setHasInputsSwapped] = React.useState<boolean>(false);

  // summary collapse logic
  const [isSummaryOpen, setIsSummaryOpen] = React.useState<boolean>(false);

  // Trade confirmation modal / Two factor warning logic
  const [isTradeConfirmationOpen, setIsTradeConfirmationOpen] = React.useState<boolean>(false);

  // Trade confirmation modal / Two factor warning logic
  const [isCreateCTLoading, setIsCreateCTLoading] = React.useState<boolean>(false);

  // Determine if the user is locked and whether to show the locked user modal
  const [userLocked, setUserLocked] = React.useState<Record<string, any>>(DEFAULT_USER_LOCKED);

  // Promotional placard logic
  const [promotionalPlacardMessage, setPromotionalPlacardMessage] = React.useState<string>("");

  const handleSubmitButtonChange = (change: Record<string, any>): void => {
    setSubmitButton({ ...submitButton, ...change });
  };

  const handleCalculating = (): void => {
    setClearButton({ text: "Calculating", isLoading: true, width: "110px" });
  };

  const handleResetClearButton = (): void => {
    setClearButton(DEFAULT_CLEAR_BUTTON);
  };

  const handleToggleSummaryCollapse = () => {
    setIsSummaryOpen((prev) => !prev);
  };

  const handleToggleTradeConfirmation = () => {
    setIsTradeConfirmationOpen((prev) => !prev);
  };

  const handleUserLockedChange = (change: Record<string, any>) => {
    setUserLocked({ ...userLocked, ...change });
  };

  /**
   * Handles the swapping of trade inputs.
   *
   * This function toggles the state to indicate that the trade inputs are being swapped.
   * It then calls a function to swap the inputs around and sets a timeout to reset the
   * state after 500 milliseconds.
   */
  const handleTradeInputSwap = () => {
    if (!hasInputsSwapped) {
      setHasInputsSwapped(true);
      dispatch(swapInputs());
      setTimeout(() => setHasInputsSwapped(false), 1500);
    }
  };

  const handlePromotionalPlacardMessage = (message: string) => {
    setPromotionalPlacardMessage(message);
  };

  const resetTradeUI = (): void => {
    if (isGuest || isUnverified) {
      handleSubmitButtonChange({ color: "salmon", text: "Verify Account", disabled: false });
    } else {
      setSubmitButton(DEFAULT_SUBMIT_BUTTON);
    }
    handleResetClearButton();
    setIsTradeConfirmationOpen(false);
    setIsCreateCTLoading(false);
  };

  /**
   * Effect hook to handle UI updates based on the loading state of the summary response.
   *
   * This hook executes whenever the `isLoading` property of `summaryResp` changes. It updates
   * the clear button's text, loading state, and width based on whether the summary is currently
   * loading. It also updates the disabled state of the main action button.
   */
  React.useEffect(() => {
    if (summaryIsLoading) {
      handleCalculating();
      handleSubmitButtonChange({ ...submitButton, disabled: true });
    } else {
      handleResetClearButton();
      handleSubmitButtonChange({ ...submitButton, disabled: false });
    }
  }, [summaryIsLoading]);

  React.useEffect(() => {
    if (ctIsLoading) {
      setIsCreateCTLoading(true);
    } else {
      setIsCreateCTLoading(false);
    }
  }, [ctIsLoading]);

  return {
    submitButton,
    clearButton,
    hasInputsSwapped,
    isSummaryOpen,
    isTradeConfirmationOpen,
    isCreateCTLoading,
    userLocked,
    promotionalPlacardMessage,
    handleSubmitButtonChange,
    handleTradeInputSwap,
    handleToggleSummaryCollapse,
    handleToggleTradeConfirmation,
    handleUserLockedChange,
    handlePromotionalPlacardMessage,
    resetTradeUI,
  };
};

export default useTradeUI;
